import React, { createContext, useContext, useEffect, useState } from 'react';

interface Settings {
  theme: 'light' | 'dark' | 'system';
  fontSize: number;
  messageAlignment: 'left' | 'right';
  sendWithEnter: boolean;
  notifications: boolean;
  soundEnabled: boolean;
  language: string;
  autoSave: boolean;
  messageHistory: number;
  compactMode: boolean;
  customBackground: string;
  accessibility: {
    highContrast: boolean;
    reducedMotion: boolean;
    screenReader: boolean;
  };
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => void;
  resetSettings: () => void;
}

const defaultSettings: Settings = {
  theme: 'system',
  fontSize: 14,
  messageAlignment: 'left',
  sendWithEnter: true,
  notifications: true,
  soundEnabled: true,
  language: 'en',
  autoSave: true,
  messageHistory: 50,
  compactMode: false,
  customBackground: '',
  accessibility: {
    highContrast: false,
    reducedMotion: false,
    screenReader: false,
  },
};

const SettingsContext = createContext<SettingsContextType | null>(null);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(() => {
    const savedSettings = localStorage.getItem('chatAppSettings');
    return savedSettings ? { ...defaultSettings, ...JSON.parse(savedSettings) } : defaultSettings;
  });

  useEffect(() => {
    localStorage.setItem('chatAppSettings', JSON.stringify(settings));
    
    // Apply theme
    document.documentElement.setAttribute('data-theme', settings.theme);
    
    // Apply font size
    document.documentElement.style.setProperty('--base-font-size', `${settings.fontSize}px`);
    
    // Apply reduced motion
    if (settings.accessibility.reducedMotion) {
      document.documentElement.style.setProperty('--transition-duration', '0s');
    } else {
      document.documentElement.style.setProperty('--transition-duration', '0.2s');
    }
    
    // Apply high contrast
    if (settings.accessibility.highContrast) {
      document.documentElement.setAttribute('data-high-contrast', 'true');
    } else {
      document.documentElement.removeAttribute('data-high-contrast');
    }
  }, [settings]);

  const updateSettings = (newSettings: Partial<Settings>) => {
    setSettings(prev => ({
      ...prev,
      ...newSettings,
      accessibility: {
        ...prev.accessibility,
        ...(newSettings.accessibility || {}),
      },
    }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, resetSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};
